import type { IEvent, IPlaylist, IPlaylistItem, IRequestPayloadTag, IResponsePlaylistItem, ITag } from "~/types";
import getSharedHeaders from "~/common/helpers/get-headers";
import { FLOW_BASED_GAMES_WITH_STATS_FROM_SM, PLAY_BASED_GAMES, PLAYLIST_TYPE } from "~/constants";
import { formatPlaylist, formatPlaylistItem, formatPlaylistItemV2 } from "~/videos/helpers/format-playlist";

import type { IRequestEventParams, IRequestPaginateParams } from "./events";
import { getCachedEventById } from "./events";

export const getCachedUserPlaylists = useMemoize(
  (opts: { limit: number; offset: number; locale?: string }): Promise<{ data: IPlaylist[]; total: number }> => getUserPlaylists(opts)
);

export function getUserPlaylists(opts: { limit: number; offset: number; locale?: string }): Promise<{ data: IPlaylist[]; total: number }> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/users/me/playlists", {
    query: {
      limit: opts.limit,
      offset: opts.offset,
      relationships: "favorite",
    },
    headers: getSharedHeaders({ locale: opts.locale }),
  }).then((response) => ({
    data: response.data.map(formatPlaylist),
    total: response.meta?.total || response.data.length,
  }));
}

export function getUserPlaylistById(id: string, options: { accessToken?: string; locale?: string }): Promise<IPlaylist> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/users/me/playlists/{ID}", {
    path: {
      ID: id,
    },
    headers: getSharedHeaders(options),
  }).then((response) => formatPlaylist(response.data));
}

export function createPlaylist(name: string): Promise<IPlaylist> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/playlists", {
    method: "POST",
    body: { name },
  }).then((response) => {
    getCachedUserPlaylists.clear();
    return formatPlaylist(response.data);
  });
}

export function updatePlaylist(id: string, name: string) {
  return useAPI(`api/v1/playlists/${id}`, { method: "PATCH", body: { name } }).then(() => {
    getCachedUserPlaylists.clear();
  });
}

export function deletePlaylist(id: string) {
  return useAPI(`api/v1/playlists/${id}`, { method: "DELETE" }).then(() => {
    getCachedUserPlaylists.clear();
  });
}

export function addTagsToPlaylist(
  tags: ITag[] | IPlaylistItem[],
  playlists: IPlaylist[],
  sourceId: string,
  sourceType?: string,
  angle?: string,
  options?: { accessToken: string; locale?: string }
) {
  const source = sourceType || "event";
  const ids = tags.map((item) => `${item.id}`);
  const requests = playlists.map((item) => {
    return useAPI(`api/v1/playlists/${item.id}/items`, {
      method: "POST",
      body: { ids, sourceId, source, angle },
      headers: getSharedHeaders(options),
    });
  });
  // TODO remove promise.all() when BE bulk request will be ready
  return Promise.all(requests);
}

export async function getEventUserPlaylistsById(
  eventId: string,
  options: IRequestPaginateParams & { accessToken?: string; locale?: string },
  query?: Partial<IRequestEventParams>,
  isSystemPlaylist?: boolean
): Promise<{ data: IPlaylist[]; total: number; bothTeamsEmpty?: boolean }> {
  const { $atlit } = useNuxtApp();

  const route = useRoute();
  const { teamId, athleteId, playlistType } = query || {};
  const event = await getCachedEventById(eventId, {
    accessToken: route.query.accessToken as string,
    customCallbacks: route.query.accessToken
      ? {
          onAuthFailure: () => {
            const expiredError = new Error("Expired link");
            expiredError.name = "ExpiredLinkError";
            throw expiredError;
          },
        }
      : undefined,
  });
  const isPlayBasedLikeEvent = PLAY_BASED_GAMES.includes(event.sport_type) || FLOW_BASED_GAMES_WITH_STATS_FROM_SM.includes(event.sport_type);
  const isSystemRequest = isSystemPlaylist && isPlayBasedLikeEvent;
  const url = isSystemRequest ? "/v1/events/{ID}/system/playlists" : "/v1/events/{ID}/playlists";
  // If a specific team or athlete is provided, use the original behavior
  if (athleteId && athleteId !== "all") {
    const athleteQuery: Record<string, any> = {
      limit: options.limit,
      offset: options.offset,
      athleteId,
    };

    // System playlists include teamId with athleteId
    if (isSystemRequest) {
      athleteQuery.teamId = teamId;
      return $atlit(url, {
        path: {
          ID: eventId,
        },
        query: athleteQuery,
        headers: getSharedHeaders(options),
      }).then((response) => ({
        data: response.data.map(formatPlaylist),
        total: response.meta?.total || response.data.length,
        bothTeamsEmpty: false,
      }));
    } else {
      return $atlit(url, {
        path: {
          ID: eventId,
        },
        query: athleteQuery,
        headers: getSharedHeaders(options),
      }).then((response) => ({
        data: response.data.map(formatPlaylist),
        total: response.meta?.total || response.data.length,
        bothTeamsEmpty: false,
      }));
    }
  }
  const team1Id = event.game_info?.team1_id;
  const team2Id = event.game_info?.team2_id;

  if (!team1Id && !team2Id) {
    return { data: [], total: 0, bothTeamsEmpty: true };
  }

  // Make requests for both teams
  const promises = [];
  if (team1Id) {
    promises.push(
      $atlit(url, {
        path: { ID: eventId },
        query: {
          limit: options.limit,
          offset: options.offset,
          type: isSystemRequest ? undefined : playlistType,
          teamId: team1Id,
        },
        headers: getSharedHeaders(options),
      })
    );
  }
  if (team2Id && playlistType !== PLAYLIST_TYPE.PRIVATE) {
    promises.push(
      $atlit(url, {
        path: { ID: eventId },
        query: {
          limit: options.limit,
          offset: options.offset,
          type: isSystemRequest ? undefined : playlistType,
          teamId: team2Id,
        },
        headers: getSharedHeaders(options),
      })
    );
  }

  const responses = await Promise.all(promises);
  const bothTeamsEmpty = responses.every((response) => response.data.length === 0);

  if (playlistType === PLAYLIST_TYPE.PRIVATE) {
    const allPlaylists = responses.flatMap((response) => response.data.map(formatPlaylist));
    const limitedPlaylists = allPlaylists.slice(0, options.limit);
    return {
      data: limitedPlaylists,
      total: responses.reduce((acc, response) => acc + (response.meta?.total || response.data.length), 0),
      bothTeamsEmpty,
    };
  }

  const data = responses[teamId === team1Id ? 0 : 1].data.map(formatPlaylist);
  const total = responses[teamId === team1Id ? 0 : 1].meta?.total || data.length;
  return {
    data,
    total,
    bothTeamsEmpty,
  };
}

export async function getPlaylistItems(id: string, options?: { accessToken?: string; locale?: string; event?: IEvent }): Promise<IPlaylistItem[]> {
  const { $atlit } = useNuxtApp();

  // If event is provided, use its permission
  if (options?.event?.permissions?.useV2PlaylistsAPI) {
    return $atlit("/v2/playlists/{ID}/items", {
      path: {
        ID: id,
      },
      headers: getSharedHeaders(options),
    }).then((response) => response.data.map((item) => formatPlaylistItemV2(item, id, options.event as IEvent)));
  }

  return useAPI<{ data: IResponsePlaylistItem[] }>(`api/v1/playlists/${id}/items`, {
    headers: getSharedHeaders(options),
  }).then((response) => response.data.map((item) => formatPlaylistItem(item, id)));
}

export function updatePlaylistItemOrder(itemId: string, position: number) {
  return useAPI(`api/v1/playlists/items/${itemId}`, { method: "PATCH", body: { position } });
}

export function deletePlaylistItem(playlistItemId: string) {
  return useAPI(`api/v1/playlists/items/${playlistItemId}`, { method: "DELETE" });
}

export function deletePlaylistItems(playlistItemIds: string[]) {
  const requests = playlistItemIds.map((id) => deletePlaylistItem(id));

  return Promise.all(requests);
}

export function editPlaylistItem(itemId: string, body: Partial<IRequestPayloadTag>): Promise<IPlaylistItem> {
  return useAPI<{ data: IResponsePlaylistItem }>(`api/v1/playlists/items/${itemId}`, { method: "PATCH", body }).then(
    // TODO: pass actual playlistId here
    (response) => formatPlaylistItem(response.data, itemId)
  );
}

export async function getPlaylistShareURL(playlist: IPlaylist, options: { baseURL: string; accessToken?: string }): Promise<string | undefined> {
  const sharedURL = useState(`shared-playlist-url-${playlist.id}`, () => "");

  // Return the url from cache if such
  if (sharedURL.value) return sharedURL.value;

  const appConfig = useAppConfig();
  let accessToken = options?.accessToken;

  if (!accessToken) {
    accessToken = await useAPI<{ data: { token: string } }>(`api/v1/playlists/${playlist.id}/share`, {
      method: "POST",
    }).then((res) => res.data.token);
  }

  // TODO: Move accessToken into the hash params
  const url = options.baseURL.includes("?") ? `${options.baseURL}&accessToken=${accessToken}` : `${options.baseURL}?accessToken=${accessToken}`;

  if (appConfig.useMockData) return Promise.resolve(url);

  const branchIO = useBranchIO();
  const linkData = {
    feature: "share-playlist",
    data: {
      playlistId: playlist.id,
      token: accessToken,
      action: "shared_playlist",
      playlist_type: playlist.type,
      $og_title: `Watch Playlist "${playlist.name}" at Pixellot!`,
      $og_image_url: "",
      $og_url: url,
      $desktop_url: url,
      $ios_url: url,
      $ipad_url: url,
      $android_url: url,
      $windows_phone_url: url,
      $blackberry_url: url,
      $fire_url: url,
    },
  };

  sharedURL.value = await branchIO.link(linkData);

  return sharedURL.value;
}
